import { EyeIcon } from '@heroicons/react/24/outline';
import noop from 'lodash/noop';
import { useTranslation } from 'react-i18next';

import { CourseAdminCard } from '@/components/shared/CourseAdminCard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import ComingSoonTooltip from '@/components/ui/custom/dataDisplay/ComingSoonTooltip';
import { Button } from '@/components/ui/daisy';
import { MaterialType } from '@/models/Book';

import MaterialMoreActions from './MaterialMoreActions';

type MaterialCardProps = {
  materialType: MaterialType;
  description?: string;
  onDelete: () => Promise<unknown>;
  isLimitedEdition?: boolean;
  onEdit?: () => void;
  onPreview?: () => void;
  isEditing?: boolean;
};

export default function MaterialCard({
  materialType,
  description,
  onDelete,
  isLimitedEdition = false,
  onEdit,
  onPreview,
  isEditing,
}: MaterialCardProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.materialsTab.card',
  });

  return (
    <CourseAdminCard>
      <CourseAdminCard.Header>
        <CourseAdminCard.Leading>
          <CourseAdminCard.TitleBlock>
            <CourseAdminCard.Title>
              {t(`names.${materialType}`)}
            </CourseAdminCard.Title>
            <ConditionalRenderer condition={description}>
              <CourseAdminCard.Description>
                {description}
              </CourseAdminCard.Description>
            </ConditionalRenderer>
          </CourseAdminCard.TitleBlock>
        </CourseAdminCard.Leading>
        <CourseAdminCard.Actions>
          <ComingSoonTooltip disabled={!onPreview}>
            <Button
              type="button"
              variant="soft"
              color="secondary"
              size="sm"
              shape="square"
              aria-label={t('preview')}
              disabled={!onPreview}
              onClick={onPreview}
            >
              <EyeIcon className="max-xl:w-3.5 w-4" />
            </Button>
          </ComingSoonTooltip>
          <ComingSoonTooltip disabled={!onEdit}>
            <CourseAdminCard.EditButton
              onClick={onEdit ?? noop}
              loading={isEditing}
            />
          </ComingSoonTooltip>
          <MaterialMoreActions
            onDelete={onDelete}
            isLimitedEdition={isLimitedEdition}
          />
        </CourseAdminCard.Actions>
      </CourseAdminCard.Header>
    </CourseAdminCard>
  );
}
